import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { formatError } from "@/ui/data/async";
import { RULE_VERSION } from "@/rules/constants";

export interface MythicCombatSessionRow {
  id: string;
  campaign_id: string;
  seed: number;
  status: string;
  current_turn_index: number;
  scene_json: Record<string, unknown>;
  updated_at: string;
}

export interface MythicCombatantRow {
  id: string;
  combat_session_id: string;
  entity_type: "player" | "npc" | "summon";
  player_id: string | null;
  character_id: string | null;
  name: string;
  lvl: number;
  offense: number;
  defense: number;
  control: number;
  support: number;
  mobility: number;
  utility: number;
  weapon_power: number;
  x: number;
  y: number;
  hp: number;
  hp_max: number;
  power: number;
  power_max: number;
  armor: number;
  resist: number;
  initiative: number;
  statuses: unknown;
  is_alive: boolean;
  updated_at: string;
}

export interface MythicTurnOrderRow {
  combat_session_id: string;
  turn_index: number;
  combatant_id: string;
}

export interface MythicActionEventRow {
  id: string;
  combat_session_id: string;
  turn_index: number;
  actor_combatant_id: string | null;
  event_type: string;
  payload: Record<string, unknown>;
  created_at: string;
}

export interface MythicCombatStateSnapshot {
  ruleVersion: string;
  session: MythicCombatSessionRow | null;
  combatants: MythicCombatantRow[];
  turnOrder: MythicTurnOrderRow[];
  events: MythicActionEventRow[];
  activeTurnCombatantId: string | null;
}

export function useMythicCombatState(campaignId: string | undefined, combatSessionId: string | null | undefined) {
  const [resolvedCombatSessionId, setResolvedCombatSessionId] = useState<string | null>(combatSessionId ?? null);
  const [session, setSession] = useState<MythicCombatSessionRow | null>(null);
  const [combatants, setCombatants] = useState<MythicCombatantRow[]>([]);
  const [turnOrder, setTurnOrder] = useState<MythicTurnOrderRow[]>([]);
  const [events, setEvents] = useState<MythicActionEventRow[]>([]);
  const [isInitialLoading, setIsInitialLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isMountedRef = useRef(true);
  const inFlightRef = useRef(false);
  const hasLoadedOnceRef = useRef(false);

  const activeTurnCombatantId = useMemo(() => {
    if (!session) return null;
    const row = turnOrder.find((t) => t.turn_index === session.current_turn_index);
    return row?.combatant_id ?? null;
  }, [session, turnOrder]);

  const fetchState = useCallback(async (): Promise<MythicCombatStateSnapshot | null> => {
    if (!campaignId) {
      if (isMountedRef.current) {
        setResolvedCombatSessionId(null);
        setSession(null);
        setCombatants([]);
        setTurnOrder([]);
        setEvents([]);
        setError(null);
        setIsInitialLoading(false);
        setIsRefreshing(false);
      }
      hasLoadedOnceRef.current = false;
      return null;
    }

    if (inFlightRef.current) return null;
    try {
      inFlightRef.current = true;
      let activeCombatSessionId = combatSessionId ?? null;
      if (!activeCombatSessionId) {
        const { data: activeSession, error: activeSessionError } = await supabase
          .schema("mythic")
          .from("combat_sessions")
          .select("id")
          .eq("campaign_id", campaignId)
          .eq("status", "active")
          .order("updated_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (activeSessionError) throw activeSessionError;
        activeCombatSessionId = (activeSession as { id?: string } | null)?.id ?? null;
      }

      if (!activeCombatSessionId) {
        if (isMountedRef.current) {
          setResolvedCombatSessionId(null);
          setSession(null);
          setCombatants([]);
          setTurnOrder([]);
          setEvents([]);
          setError(null);
          setIsInitialLoading(false);
          setIsRefreshing(false);
        }
        hasLoadedOnceRef.current = false;
        return null;
      }

      if (isMountedRef.current) {
        if (hasLoadedOnceRef.current) {
          setIsRefreshing(true);
        } else {
          setIsInitialLoading(true);
        }
        setResolvedCombatSessionId(activeCombatSessionId);
        setError(null);
      }

      const [{ data: s, error: sErr }, { data: c, error: cErr }, { data: t, error: tErr }, { data: e, error: eErr }] =
        await Promise.all([
          supabase
            .schema("mythic")
            .from("combat_sessions")
            .select("id,campaign_id,seed,status,current_turn_index,scene_json,updated_at")
            .eq("id", activeCombatSessionId)
            .eq("campaign_id", campaignId)
            .maybeSingle(),
          supabase
            .schema("mythic")
            .from("combatants")
            .select("*")
            .eq("combat_session_id", activeCombatSessionId)
            .order("initiative", { ascending: false })
            .order("name", { ascending: true }),
          supabase
            .schema("mythic")
            .from("turn_order")
            .select("*")
            .eq("combat_session_id", activeCombatSessionId)
            .order("turn_index", { ascending: true }),
          supabase
            .schema("mythic")
            .from("action_events")
            .select("*")
            .eq("combat_session_id", activeCombatSessionId)
            .order("created_at", { ascending: true })
            .limit(500),
        ]);

      if (sErr) throw sErr;
      if (cErr) throw cErr;
      if (tErr) throw tErr;
      if (eErr) throw eErr;

      const sessionRow = (s ?? null) as unknown as MythicCombatSessionRow | null;
      const combatantRows = (c ?? []) as unknown as MythicCombatantRow[];
      const turnRows = (t ?? []) as unknown as MythicTurnOrderRow[];
      const eventRows = (e ?? []) as unknown as MythicActionEventRow[];
      const activeTurnCombatantId = sessionRow
        ? turnRows.find((row) => row.turn_index === sessionRow.current_turn_index)?.combatant_id ?? null
        : null;

      if (isMountedRef.current) {
        setSession(sessionRow);
        setCombatants(combatantRows);
        setTurnOrder(turnRows);
        setEvents(eventRows);
      }

      return {
        ruleVersion: RULE_VERSION,
        session: sessionRow,
        combatants: combatantRows,
        turnOrder: turnRows,
        events: eventRows,
        activeTurnCombatantId,
      };
    } catch (e) {
      const msg = formatError(e, "Failed to load combat state");
      if (isMountedRef.current) setError(msg);
      return null;
    } finally {
      inFlightRef.current = false;
      hasLoadedOnceRef.current = true;
      if (isMountedRef.current) {
        setIsInitialLoading(false);
        setIsRefreshing(false);
      }
    }
  }, [campaignId, combatSessionId]);

  useEffect(() => {
    isMountedRef.current = true;
    if (campaignId) {
      hasLoadedOnceRef.current = false;
      setIsInitialLoading(true);
      setIsRefreshing(false);
    }
    fetchState();
    return () => {
      isMountedRef.current = false;
    };
  }, [campaignId, combatSessionId, fetchState]);

  useEffect(() => {
    if (!campaignId || !resolvedCombatSessionId) return;
    const pollMs = session?.status === "active" ? 2500 : 4500;
    const interval = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState === "hidden") {
        return;
      }
      void fetchState();
    }, pollMs);
    return () => clearInterval(interval);
  }, [campaignId, resolvedCombatSessionId, fetchState, session?.status]);

  return {
    ruleVersion: RULE_VERSION,
    resolvedCombatSessionId,
    session,
    combatants,
    turnOrder,
    events,
    activeTurnCombatantId,
    isInitialLoading,
    isRefreshing,
    isLoading: isInitialLoading,
    error,
    refetch: fetchState,
  };
}
